import React from 'react';
import { ExternalLink, Building2, Home, MapPin, Briefcase } from 'lucide-react';
import { portfolio } from '../data/mock';
import { Button } from './ui/button';

const categoryIcons = {
  'Property Management': Building2,
  'Real Estate': MapPin,
  'Real Estate Agent': MapPin,
  'Vacation Rentals': Home,
  'Business Management': Briefcase
};

const PortfolioSection = () => {
  return (
    <section id="portfolio" className="py-24 bg-navy-900 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-navy-700/30 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-silver-600/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6">
            <span className="w-2 h-2 bg-silver-300 rounded-full" />
            <span className="text-silver-200 text-sm font-semibold uppercase tracking-wider">Portfolio</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Companies & Affiliations
          </h2>
          <p className="text-lg text-silver-300 max-w-3xl mx-auto">
            Explore the companies and partnerships where Sean provides expertise in real estate, property management, and hospitality.
          </p>
        </div>

        {/* Portfolio Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {portfolio.map((item, index) => {
            const Icon = categoryIcons[item.category] || Building2;
            return (
              <a
                key={item.id}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-silver-400/30 transition-all duration-500 hover:transform hover:scale-[1.02]"
              >
                {/* Shine Effect on Hover */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 transform -skew-x-12" />

                <div className="relative z-10">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-14 h-14 bg-navy-700 rounded-xl flex items-center justify-center group-hover:bg-navy-600 transition-colors">
                      <Icon className="w-7 h-7 text-silver-300" />
                    </div>
                    <div className="flex items-center gap-2 text-silver-400 group-hover:text-white transition-colors">
                      <span className="text-sm">Visit Site</span>
                      <ExternalLink className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    </div>
                  </div>

                  {/* Category Badge */}
                  <span className="inline-block px-3 py-1 bg-navy-700/50 rounded-full text-xs text-silver-300 font-medium mb-4">
                    {item.category}
                  </span>

                  {/* Content */}
                  <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-silver-100 transition-colors">
                    {item.name}
                  </h3>
                  <p className="text-silver-400 group-hover:text-silver-300 transition-colors">
                    {item.description}
                  </p>

                  {/* URL Preview */}
                  <div className="mt-6 pt-6 border-t border-white/10">
                    <span className="text-sm text-silver-500 group-hover:text-silver-400 transition-colors">
                      {item.url.replace('https://', '')}
                    </span>
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PortfolioSection;
